/* eslint-disable @typescript-eslint/no-unused-vars */
import styled from 'styled-components';
import { Company } from 'Types/CompaniesTypes';
import HeadingWithShowAll from 'Components/globalSearch/components/HeadingWithShowAll';
import SectionHeading from 'Components/globalSearch/components/SectionHeading';
import {
  NavigateFunction, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import { selectIsPagination } from 'Selector/GlobalSearchResultsSelector';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { FEED_GILROY_ROUTE, ONBOARDING, SEARCH_ROUTE } from 'Utils/Constants';
import { formatNumberWithCommas, SlicerResultPage, valueToObject } from 'Utils/UtilityFunctions';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import SearchCompanySkeletonBlock from '../skeleton/SearchCompanySkeletonBlock';
import CompanyCardLarge from './CompanyCardLarge';
import CompanySkeleton from '../skeleton/CompanySkeleton';
import AddCompanyLimitPopup from '../modal/AddCompanyLimitPopup';

type Props = {
  listView?: boolean,
  heading?: string,
  hideDefaultBackground?: boolean,
  companiesCollection?: Company[],
  loading?: boolean,
  query?: string,
  showLoaderForLessResults?: boolean,
  numberOfLoaderForLessResults?: number,
  companyCount?: Company[],
  count?: number,
  setCompanyTop?: (a: string) => void
  disableNavigation?: boolean,
  inGlobalSearch?: boolean,
  hideShowAll?: boolean,
  isPaginationOverride?: boolean,
} & typeof defaultProps;

const defaultProps = {
  listView: false,
  heading: '',
  query: '',
  hideDefaultBackground: false,
  loading: false,
  companiesCollection: [] as Company[],
  showLoaderForLessResults: false,
  numberOfLoaderForLessResults: 0,
  companyCount: [] as Company[],
  count: 0,
  setCompanyTop(a: string) { },
  disableNavigation: false,
  inGlobalSearch: false,
  hideShowAll: false,
};

const StyleWrapper = styled.div`
  .companies-margin{
    margin-left:12px;
    margin-right:12px;
  }
  .Grid-width-row {
    padding-right: 1px;
  }
  .fancy-box {
    border-radius: 22px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 40px 50px 40px 50px;
    margin-top: 50px;
    h1 {
      font-family: "Roboto Slab";
      color: #424b50;
      font-size: 28px;
    }
    p {
      font-family: "Roboto";
      color: #5e7079;
    }
    input {
      font-size: 16px;
      padding: 7px 12px;
    }
    label {
      font-size: 12px;
    }
  }
`;

const CompaniesSection = function CompaniesSection(props: Props) {
  const [searchParams] = useSearchParams();
  const extendedSections = ['person', 'company', 'article'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const slicerNumber: number = SlicerResultPage();
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const navigate: NavigateFunction = useNavigate();
  const isPagination: boolean = useSelector(selectIsPagination);
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const {
    heading, hideDefaultBackground, companiesCollection, loading, query,
    showLoaderForLessResults,
    numberOfLoaderForLessResults,
    companyCount, disableNavigation,
    count, setCompanyTop, listView, inGlobalSearch, hideShowAll,
    isPaginationOverride,
  } = props;
  const paginationActive = typeof isPaginationOverride === 'boolean'
    ? isPaginationOverride
    : isPagination;
  const shouldShowPaginationSkeleton = paginationActive
    && (isDetailedSearch || typeof isPaginationOverride === 'boolean');
  const host = window.location.href;
  const formattedCount = formatNumberWithCommas(count);
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const createCompaniesRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_companies_see_all_clicked', 0, {
        host,
      }),
    ));
    window.scrollTo(0, 0);
    setCompanyTop('company');
    return navigate(`${pathname}?query=${query}&extend=company`, { state: { enableBackButton: true, previousPath } });
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  const loadersArray = [];
  if (showLoaderForLessResults) {
    for (let i = 0; i < numberOfLoaderForLessResults; i += 1) {
      loadersArray.push(<CompanySkeleton />);
    }
  }

  return (
    companiesCollection.length > 0 || loading ? (
      <StyleWrapper>
        {!window.location.pathname?.includes(`/${ONBOARDING}`) && <AddCompanyLimitPopup />}
        {
          loading && !isDetailedSearch
            ? (
              <SearchCompanySkeletonBlock
                sliceFlag={!isDetailedSearch}
                inGlobalSearch={inGlobalSearch}
              />
            )
            : (
              <>
                <div className={`${hideDefaultBackground ? '' : 'fancy-box'}`}>
                  <HeadingWithShowAll
                    inGlobalSearch={inGlobalSearch}
                    isShowAllHidden={hideShowAll || (isDetailedSearch ? true
                      : companyCount && companyCount.length <= slicerNumber)}
                    leftHeading={(
                      <SectionHeading customClassName="global-search-heading">{`${heading} (${formattedCount})`}</SectionHeading>)}
                    onClick={createCompaniesRoute}
                    onSeelessClick={navigateToSearchResult}
                    isDetailedSearch={!hideShowAll && isDetailedSearch}
                  />
                  <div>
                    <Row
                      className={classNames('Grid-width-row col-six-fix-md', {
                        gridUpdated: inGlobalSearch,
                      })}
                      gutter={[{
                        xs: 16, lg: 40, md: 32, sm: 32,
                      }, 8]}
                    >
                      {companiesCollection?.map((company) => (
                        <Col span={6} xs={12} md={4} lg={4} sm={4} className="gutter-row list-perf-item" key={company.companyId}>
                          <CompanyCardLarge
                            companyData={company}
                            isSearch
                            listView={listView}
                            disableNavigation={disableNavigation}
                            showDrawerIcon={!!company.companyNotes}
                            showSapIcon={!!company.accountPlanAllowed}
                            inGlobalSearch
                          />
                        </Col>

                      ))}
                    </Row>
                  </div>
                </div>
                {shouldShowPaginationSkeleton
                  && <SearchCompanySkeletonBlock inGlobalSearch={inGlobalSearch} />}

              </>
            )
        }
      </StyleWrapper>
    ) : (
      null
    )
  );
};

CompaniesSection.defaultProps = defaultProps;
export default CompaniesSection;
